import apiClient from '../utils/axios-config';
import {
  PaginatedNotificationResponse,
  UnreadCountResponse,
} from '../types/notification';

const API_BASE_URL =
  'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

/**
 * Fetch the authenticated user's notifications in reverse chronological order.
 *
 * @param limit  Number of notifications per page (default 20, max 50).
 * @param lastKey  Opaque pagination cursor returned by a previous call.
 * @returns Paginated notification response with `notifications`, `lastKey`, and `hasMore`.
 */
export async function getNotifications(
  limit?: number,
  lastKey?: string,
): Promise<PaginatedNotificationResponse> {
  const params = new URLSearchParams();
  if (limit !== undefined) {
    params.append('limit', String(limit));
  }
  if (lastKey) {
    params.append('lastKey', lastKey);
  }

  const query = params.toString();
  const url = `${API_BASE_URL}/notifications${query ? `?${query}` : ''}`;

  const response = await apiClient.get<PaginatedNotificationResponse>(url);
  return response.data;
}

/**
 * Fetch the unread notification count for the authenticated user.
 *
 * @returns Object with a `count` field.
 */
export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const response = await apiClient.get<UnreadCountResponse>(
    `${API_BASE_URL}/notifications/unread-count`,
  );
  return response.data;
}

/**
 * Mark specific notifications as read.
 *
 * @param ids  Array of notification IDs to mark as read.
 */
export async function markAsRead(ids: string[]): Promise<void> {
  await apiClient.patch(`${API_BASE_URL}/notifications/mark-read`, { ids });
}

/**
 * Mark all of the authenticated user's notifications as read.
 */
export async function markAllAsRead(): Promise<void> {
  await apiClient.patch(`${API_BASE_URL}/notifications/mark-all-read`);
}

const notificationService = {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
};

export default notificationService;
